package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/create")
@Composable
fun CreatePage() {
    BlogLayout(
        PageProperties(
            "Coroutines Create",
            "Coroutine Builder (runBlocking, launch, async), Kotlin offers a variety of functions you can use to create coroutines."
        ),
        BlogProperties("Coroutines", "Suspending function | Coroutine Builder (create Coroutines)")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Blocking",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Blocking occurs when a thread is completely occupied and unable to perform other tasks until it finishes. If this happens on the main thread, the UI becomes unresponsive.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Suspend()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The suspend modifier is used to mark a function or lambda expression as a coroutine. It indicates that the function can be suspended, allowing other coroutines to run while it’s waiting for a non-blocking operation to complete.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We may consider a suspend function as a regular function that can be paused and resumed later, without blocking the main thread after its task is done, which means we can put a time-consuming task into the function and wait for it to complete.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Suspending functions are special functions that perform asynchronous operations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "They must be called inside a coroutine scope (like viewModelScope.launch or LaunchedEffect).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Suspending functions can be used inside coroutines just like regular functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, waiting functions can be used for network requests or database operations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By default, suspend functions are executed on a background thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "These functions can be used within coroutines and are a central part of the coroutine ecosystem.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a suspending function is called, it runs in a separate coroutine, and the calling coroutine is suspended until the execution of the suspending function completes. (They can execute a long running operation and wait for it to complete without blocking.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A suspend function will only return once its execution is complete, meaning that any code following a call to a suspend function will not be executed until the function has finished running.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "During the suspension, the calling thread is free to execute other tasks, making suspending functions an efficient way to handle long-running or blocking I/O operations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Suspending functions can be called from within other suspending functions or from non-suspending functions using a coroutine builder such as launch or async.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Can only be called from another suspend function or a coroutine.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s a suspending function that can be cancelled. If the coroutine is cancelled, the provided block is executed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Suspend Function Work",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Suspend functions take advantage of the underlying mechanics of Kotlin coroutines, which include cooperative multitasking. When a coroutine invokes a suspend function, it does not pause the entire thread. Instead, the coroutine is suspended, allowing the thread to conduct additional tasks. When the asynchronous operation in the suspend function is completed, the coroutine is resumed and can continue from where it left off. This cooperative nature enables you to write asynchronous code in a sequential and structured manner, just as if it were synchronous.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://aliblogs.lovable.app/blog/822a08c5-4da3-4356-bbb2-49d6b70a54c0
            SpanText(
                text = "How does suspend functions work internally?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the Kotlin compiler processes a suspend function or when the Kotlin compiler encounters a suspendfunction, it transforms the function using a technique called Continuation-Passing Style (CPS) to manage asynchronous execution.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.8.em).toAttrs()) {
                        SpanText(
                            text = "Continuation Mechanism - Resumes the function from where it was paused, preserving execution context.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )

                        Ul {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Suspend functions use a concept called continuations. When a suspend function is called, it doesn’t execute all at once. Instead, it may pause its execution and save its state at a certain point. This saved state is called a continuation.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            KotlinCode(
                                code = """
                                     interface Continuation<in T> {
                                         val context: CoroutineContext
                                         fun resumeWith(result: Result<T>)
                                     }
                                """.trimIndent()
                            )

                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "The continuation allows the function to resume later from where it was paused, preserving the function’s state, local variables, and the execution context.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "When delay(1000) is called, the function suspends, and the continuation is saved. After 1000 milliseconds, the continuation resumes execution.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            KotlinCode(
                                code = """
                                        suspend fun fetchData(): String {
                                            delay(1000)  // Suspend here
                                            return "Data"
                                        }
                                    """.trimIndent()
                            )
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.8.em).toAttrs()) {
                        SpanText(
                            text = "State Machine -  Manages the function’s execution state transitions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )

                        Ul {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "The compiler generates a state machine for the suspend function. This state machine keeps track of the function’s execution state and manages the suspension and resumption process without blocking threads, ensuring efficient execution.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.8.em).toAttrs()) {
                        SpanText(
                            text = "Coroutine Context",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                                .fontWeight(FontWeight.Bold)
                        )

                        Ul {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Coroutine context is used to manage which thread or dispatcher the coroutine should run on. When a suspend function is called, it’s executed within a coroutine context that handles switching between threads as needed. (Handles threading and dispatching, allowing seamless switching between different threads or dispatchers.)",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "For example, Dispatchers.IO can be used for offloading tasks to a background thread, while Dispatchers.Main is used for UI updates.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }
                        }
                    }
                }
            }

            Li(Modifier.padding(top = 1.em).toAttrs()) {
                SpanText(
                    text = "How Does Suspension and Resumption Work Internally?",
                    modifier = Modifier.fontSize(FontSize.Large)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When a coroutine reaches a suspend function:",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The current state is captured and stored in a continuation.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The thread executing the coroutine is released to perform other tasks. The suspend function signals when it is ready for the coroutine to resume.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When the coroutine is ready to resume:",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The continuation restores the saved state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The coroutine resumes execution from the exact point where it was suspended. The thread is reallocated to continue running the coroutine.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "What is a suspend function? How is it different from a regular async call?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A suspend function can pause execution without blocking the thread. The compiler transforms it using a state machine with a Continuation parameter — it saves the call stack and resumes when ready.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = " Unlike callbacks or RxJava, the code looks synchronous but runs asynchronously. Unlike threads, suspending is cheap — no OS context switch.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // Looks synchronous, runs async
                    suspend fun fetchUser(id: String): User {
                        val token = authRepo.getToken()     // suspends here
                        return apiService.getUser(token, id) // suspends here
                    }

                    // Under the hood, compiler generates roughly:
                    fun fetchUser(id: String, cont: Continuation<User>) {
                        // state machine with labels 0, 1, 2...
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "suspend vs blocking",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "suspend functions can be paused without blocking a thread. By contrast, blocking functions (like Thread.sleep) block the thread, preventing other coroutines from running.",
                        modifier = Modifier.fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Suspension (The Pause)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The act of saving the current state (local variables and the next instruction) and freeing the Thread to do other work.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Call a suspend function from another suspend function",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When we call a suspend function from another suspend function, the first function will suspend execution until the second function completes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This can be used to our advantage to create asynchronous code that is easy to read and debug.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Explanation of Each Suspending Function",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "delay(timeMillis: Long)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This suspending function pauses the coroutine for a specified amount of time without blocking the thread. During the delay, the thread can run other coroutines.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "yield()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Suspends the coroutine and gives control back to the coroutine dispatcher, allowing other coroutines to run (Suspends the coroutine to allow other coroutines to run). It’s useful in long-running loops where you want to ensure other coroutines get a chance to execute. Commonly used in long-running tasks to prevent blocking.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "eld function in scenarios where you have multiple coroutines running concurrently and you want to ensure that no single coroutine monopolizes the execution thread.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "withContext(context: CoroutineContext)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "switches the coroutine context, such as changing the dispatcher to run on a different thread pool. The coroutine waits for the block to complete and returns the result. ",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "withTimeout(timeMillis: Long)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Runs a block of code with a timeout. If the code block takes longer than the specified time, it throws a TimeoutCancellationException. Commonly used to set a timeout for network requests.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "withTimeoutOrNull(timeMillis: Long)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Similar to withTimeout, but instead of throwing an exception, it returns null if the code block doesn’t complete within the specified time. Commonly used to set a timeout for database operations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    fun main() = runBlocking {
                        try {
                            withTimeout(1300) {
                                repeat(1000) { i ->
                                    println(i) //Working
                                    delay(500)
                                }
                            }
                        } catch (e: TimeoutCancellationException) {
                            println("Task took too long and was cancelled!")
                        }
                    }

                    /*
                    output
                    Working 0 ...
                    Working 1 ...
                    Working 2 ...
                    Task took too long and was cancelled!
                    */
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Timeouts and Cancellation
                    
                    val result = withTimeoutOrNull(5.seconds) {
                        fetchData()
                    }
                    if (result == null) {
                        println("Operation timed out, returning default value")
                    }
                    
                    try {
                        withTimeout(5.seconds) { // Cancels after 5 seconds
                            fetchData()
                        }
                    } catch (e: TimeoutCancellationException) {
                        println("Operation timed out")
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coarse Whole Request Deadline",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Because code in one coroutine runs sequentially and structured concurrency guarantees that no child coroutine is fire and forget, the whole operation can be wrapped in a withTimeout block.",
                            modifier = Modifier.fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = $$"""
                        fun main() {
                            runCatching {
                                withTimeout(1000) {
                                    //function call
                                }.onFailure { e ->
                                    println("processing cancelled for: $e")
                        		}
                            }
                        }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "awaitAll()",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Awaits for multiple `Deferred` values concurrently. Commonly used in repositories to perform concurrent network requests. This ensures that if any single task fails, the entire group is handled properly (canceled and propagated) under the structured concurrency model.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "awaitAll() handle errors: It is fail-fast. If one task in the list throws an exception, awaitAll() will immediately propagate that failure and cancel all other sibling coroutines in that scope to save resources.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "joinAll(vararg jobs: Job)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Waits for all provided jobs to be completed. It’s useful when you have multiple coroutines running and you want to ensure they all finish before proceeding. Commonly used in ViewModel to wait for multiple tasks to complete.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    fun main() = runBlocking {
                        // Example of delay
                        val job1 = launch {
                            delay(1000L)  // Simulate a 1-second non-blocking delay
                            println("Job1: Completed after delay")
                        }

                        // Example of yield
                        val job2 = launch {
                            for (i in 1..5) {
                                yield()  // Yield control to other coroutines
                            }
                            println("Job2: Task completed")
                        }

                        // Waiting for all jobs to complete using joinAll
                        joinAll(job1, job2)

                        // Example of withContext
                        val result = withContext(Dispatchers.Default) {
                            "Result from withContext"
                        }
                        println(result)

                        // Example of withTimeout
                        try {
                            withTimeout(2000L) {  // Timeout after 2 seconds
                                println("withTimeout: Starting long-running task")
                                delay(3000L)  // Simulate a long task that exceeds the timeout
                                println("withTimeout: This will not be printed due to timeout")
                            }
                        } catch (e: TimeoutCancellationException) {
                            println("withTimeout: Task timed out")
                        }

                        // Example of withTimeoutOrNull
                        val resultOrNull = withTimeoutOrNull(1000L) {
                            println("withTimeoutOrNull: Starting task")
                            delay(1500L)  // Simulate a task that exceeds the timeout
                            "This will not be returned due to timeout"
                        }

                        println("Main: All jobs are completed")
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Use: It is suitable for cases where you need to perform background operations without waiting for a specific outcome.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Continuations",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "At their core, coroutines are built on a concept known as continuations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines are transformed at compile-time into state machines that use Continuation objects to handle suspension and resumption.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A continuation represents a point in a program where execution can be paused and later resumed. When a coroutine suspends, its current state (including local variables, the call stack, and program counter) is captured in a continuation object. This object can later be used to resume execution from the same point.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Kotlin, the compiler transforms suspending functions into a state machine using these continuations. Each suspending function is essentially a function that takes an implicit Continuation parameter, which acts as a callback to resume execution.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        public interface Continuation<in T> {
                            public val context: CoroutineContext
                            public fun resumeWith(result: Result<T>)
                        }
                    """.trimIndent()
                )

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "T: The type of the result that the continuation will produce.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "context: Holds information about the coroutine's context, such as the dispatcher.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "resumeWith: A function that resumes the execution of the coroutine with a given result, which can be either a successful value or an exception.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        suspend fun example () {
                            println("Start")
                            delay (1000) // suspension point println("End")
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        fun example (continuation: Continuation<Unit>) {
                            printin ("Start")
                            // Save state and return control to the caller
                            continuation.resumeWith (Result. success (Unit))
                            printin ("End")
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This mechanism ensures that coroutines can pause and resume without relying on traditional thread stacks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }


            }

            SpanText(
                text = "How Continuations Work in Coroutines",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When a suspending function suspends, it doesn’t block the thread. Instead, it saves its current state into a continuation object. The coroutine infrastructure uses this continuation to resume the function later.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Coroutine Builder",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "These builders simplify the process of launching and managing coroutines, providing different options based on specific use cases.. Two commonly used builder functions are:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "CoroutineScope",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                SpanText(
                    text = "CoroutineScope, on the other hand, is a container for coroutines. It holds a CoroutineContext, which usually includes a Job that manages the lifecycle of all coroutines launched within the scope. A CoroutineScope is a manager, and each Job is a worker. The manager can hire workers (launch new coroutines), and shutting down the manager (i.e. cancelling the scope’s Job) sends everyone home. When dealing with coroutines, scope refers to the context in which a coroutine runs and is controlled. Its purpose is to manage and monitor the coroutines you create inside of it. CoroutineScope holds a reference to the CoroutineContext so that it encapsulates other coroutine tasks within. CoroutineScope is a custom coroutine scope that is typically associated with a specific component's lifecycle, such as an Activity or Fragment. By creating a coroutine scope tied to a component’s lifecycle, you ensure that coroutines launched within that scope are automatically cancelled when the component is destroyed or no longer needed, preventing memory leaks. There are primarily two types of scopes relevant to coroutines: GlobalScope and CoroutineScope.",
                    modifier = Modifier.padding(topBottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(topBottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "GlobalScope: ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    SpanText(
                        text = "It is a top-level scope that is not tied to any specific lifecycle or context. Coroutines launched in global scope continue to execute until they are complete or until the application terminates. It’s generally recommended to avoid using GlobalScope in production code because coroutines launched in GlobalScope can potentially run indefinitely and may lead to resource/memory leaks or unintended behavior. Coroutines launched within the GlobalScope are not tied to the lifecycle of any specific component (e.g., Activity or Fragment) and continue to execute until they are explicitly cancelled. While GlobalScope is convenient for launching coroutines that have a global scope and long-running tasks, it's generally not recommended for Android applications because it can lead to memory leaks if coroutines are not properly cancelled when no longer needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineScope (someCoroutineContext): ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    SpanText(
                        text = "Most often, the someCoroutineContext is a dispatcher (e.g. Dispatchers.IO or Dispatchers.Main) plus a Job or SupervisorJob. ",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.8.em).toAttrs()) {
                            SpanText(
                                text = "Lifecycle for Coroutines",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .fontWeight(FontWeight.Bold)
                            )

                            Ul {
                                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "A CoroutineScope defines a lifecycle, a lifetime, for Coroutines that are built and launched from it. A CoroutineScope lifecycle starts as soon as it is created and ends when it is canceled or when it associated Job or SupervisorJob finishes. When that happens, the CoroutineScope is no longer active.",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }

                                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                    SpanText(
                                        text = "Any launch- or async-Coroutine built from a CoroutineScope will, if it is still running, be canceled when itsCoroutineScope lifecycle ends.",
                                        modifier = Modifier
                                            .fontSize(FontSize.Medium)
                                    )
                                }

                                KotlinCode(
                                    code = """
                                        val scope: CoroutineScope = ...
                                        scope.launch {
                                            while(true) {
                                                delay(100)
                                            }
                                        }
                                        scope.cancel()
                                    """.trimIndent()
                                )
                            }
                        }
                    }

                    Ul {
                        Li(Modifier.padding(topBottom = 0.8.em).toAttrs()) {
                            SpanText(
                                text = "Controlling the lifecycle",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                                    .fontWeight(FontWeight.Bold)
                            )
                        }

                        Ul {
                            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "We use a so-called top-most CoroutineScope when we want to control the lifecycle of the Coroutines we launch, so that we can cancel them and handle any exceptions.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "The most common way to do this, is to construct a top-most Coroutine ourselves. This can be done by just calling CoroutineScope(someCoroutineContext).",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                )
                            }

                            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                                SpanText(
                                    text = "Another way to construct a top-most one is to tie it to the lifecycle of another class of our own making, either by inheritance or by delegation/aggregation.",
                                    modifier = Modifier
                                        .fontSize(FontSize.Medium)
                                        .fontWeight(FontWeight.Bold)
                                )
                            }


                            KotlinCode(
                                code = """
                                    //Living at the edge

                                    class MovieListViewModel(
                                            private val dataSource: DataSource,
                                            private val context: CoroutineContext = Dispatchers.Main
                                        ): ViewModel() {
                                            private val scope = CoroutineScope(context + SupervisorJob())

                                            //val scope = CoroutineScope(Dispatchers.IO + SupervisorJob())

                                            //scope.cancel()
                                    }
                    """.trimIndent()
                            )
                        }
                    }
                }
            }

            SpanText(
                text = "CoroutineScope important",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.5.em).toAttrs()) {
                    SpanText(
                        text = "A CoroutineScope defines the lifecycle of coroutines. It's crucial for Structured Concurrency. When a CoroutineScope is cancelled, all coroutines launched within it are automatically cancelled, preventing memory leaks and ensuring resources are properly released, especially with Android UI components.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }
            }

            KotlinCode(
                code = """
                    //CoroutineScope and Flows
                    val scope: CoroutineScope = ...
                    scope.launch {
                         counterFlow.collect { counter ->
                             ...
                         }
                    }

                    //CoroutineScope and Channels

                    //Switching CoroutineScopes

                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    var result = 0

                    CoroutineScope(Dispatchers.IO).launch {
                        for (i in 0 until 100) {
                            kotlinx.coroutines.delay(10)
                            result++
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Launching Coroutines (fire-and-forget)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                SpanText(
                    text = "This is a scope tied to a specific coroutine builder, such as launch or async. When the associated object is destroyed or when the scope is canceled, all coroutines launched within that scope are automatically canceled. When the thread is closed, all the coroutines associated with that thread are also closed / destroyed.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "launch",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use: It is suitable for cases where you need to perform background operations without waiting for a specific outcome.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "launch can be only used from inside a coroutine scope.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It launches a new coroutine without blocking a current thread.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It launches a new coroutine concurrently with the rest of the code, which continues to work independently.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Delay is a special suspending function. It suspends the coroutine for a specific time.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It returns a reference to the job object, which represents the coroutine’s lifecycle. Using a job object we can cancel the coroutine or wait for the coroutine to finish and track its status. Launches a new coroutine without blocking the current thread and inherits the thread & coroutine scope of the immediate parent coroutine.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Starts a new coroutine and runs operations out of order.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "You don’t have to wait for results, so it’s often used for tasks that don’t have thread dependencies.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Here, we use GlobalScope.launch to create a coroutine with a global scope. Alternatively, we can use launch to create a coroutine with a coroutine scope.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "launch is typically used for fire-and-forget tasks where you don't need to receive a result from the coroutine, such as performing background work or updating UI elements.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "async",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "How do you achieve parallelism using Kotlin Coroutines? Parallelism in coroutines can be achieved using the async builder along with await to await the results. Multiple asynchronous tasks can be started concurrently, and their results can be combined when needed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use: When making two or more network call in parallel, but you need to wait for the answers before computing the output, If you use async and do not wait for the result, it will work exactly the same as launch.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "CoroutineScope allows launching multiple async tasks, and the app won’t crash even if one of the responses is null.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The async is used to start new coroutine and when you want to perform a task asynchronously and also need to retrieve a result or handle exceptions from that task.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The exceptions can be handled with try/catch or onFailure to prevent crashes and unexpected behavior.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "async{} returns an instance of Deferred<T>, which has an await() function that returns the result of the coroutine like we have future in Java in which we do future.get() to the get the result.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This function returns an instance of Deferred, which can be thought of as a promise of a result that will be available sometime in the future.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It initiates parallel operations and allows you to wait for the results of these operations in bulk. Each operation returns a value and you can access these values later with await.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "async() also creates a coroutine and also returns a type of Job. However, the specific type that async() returns is Deferred, a sub-type of Job.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The catch is that await() is itself a suspend function.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The async builder function allows for concurrent execution of multiple coroutines by suspending the current coroutine until the value it is waiting for is available.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The async builder function is typically used in combination with the await() function to wait for the completion of the concurrent computation and obtain its result.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Note that the async builder function creates a new coroutine, so it should only be used from within a coroutine scope such as coroutineScope or withContext. It's also important to remember that the async builder function is a coroutine builder, which means that it must be called from within a coroutine.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Async builder returns instance of Deferred, await, awaitAll, and join operation needs to call on deferred instance to execute async block. Deferred nothing but the implementation of Job. Functions await and join are suspending functions that needs to be called from other Coroutine or suspending function.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    KotlinCode(
                        code = """
                            val (topRatedMoviesResult, nowPlayingMoviesResult, popularMoviesResult) = awaitAll(
                                async { repository.getTopRatedMovies(MoviesApi.LANG_ENG, 1) },
                                async { repository.getNowPlayingMovies(MoviesApi.LANG_ENG, 1) },
                                async { repository.getPopularMovies(MoviesApi.LANG_ENG, 1) }
                            )
                        """.trimIndent()
                    )

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When you call to cancel on deferred object, cancels async block.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "async is commonly used when you need to perform a background task and retrieve its result, such as fetching data from a network or performing a CPU-intensive computation asynchronously.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lazily started async",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "CoroutineStart.LAZY: A coroutine start option in Kotlin that delays the start of a coroutine until it’s explicitly needed. When you launch a coroutine with CoroutineStart.LAZY, it won’t start running immediately.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Just like we can initialise a variable lazily, we can start a coroutine also lazily. In this case, coroutine will be started only when it’s result is needed, that is, it’s await or start function is being called.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "How async Exceptions Work",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Exceptions inside async are captured.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The exception reappears when we call await().",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "CoroutineExceptionBuilder does not catch errors inside async errors.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "awaitall() throws the first exception and cancels the rest.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Use supervisorScope() for independent parallel tasks.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Always rethrow CancellationException.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                //https://github.com/sunildhiman90/FirebaseKMP-Compose
                KotlinCode(
                    code = """
                        val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
                        
                        private suspend fun launchWithAwait(block : suspend  () -> Unit) {
                            scope.async {
                                block()
                            }.await()
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    fun calculateTimeTakenInSynchronousLazyCalls() = runBlocking {
                        val time = measureTimeMillis {
                            val one = async(start = CoroutineStart.LAZY) { doSomethingUsefulOne() }
                            val two = async(start = CoroutineStart.LAZY) { doSomethingUsefulTwo() }
                        }
                    }

                    fun calculateTimeTakenInAsynchronousLazyCalls() = runBlocking {
                        val time = measureTimeMillis {
                            val one = async(start = CoroutineStart.LAZY) { doSomethingUsefulOne() }
                            val two = async(start = CoroutineStart.LAZY) { doSomethingUsefulTwo() }
                            one.start()
                            two.start()
                        }
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                     //launch
                    import kotlinx.coroutines.*

                    fun main() {
                        // Start a new coroutine
                        val job = GlobalScope.launch {
                            // Coroutine body
                            delay(1000) // Simulate some work
                            println("Coroutine is running")
                        }

                        // Wait for the coroutine to complete
                        runBlocking {
                            job.join()
                        }
                    }

                    //async
                    import kotlinx.coroutines.*

                    fun main() {
                        // Start a new coroutine
                        val deferred = GlobalScope.async {
                            // Coroutine body
                            delay(1000) // Simulate some work
                            "Result from coroutine"
                        }

                        // Wait for the coroutine to complete and retrieve the result
                        runBlocking {
                            val result = deferred.await()
                            println(result)
                        }
                    }
                    """.trimIndent()
                )
            }

            KotlinCode(
                code = """
                    //Aggregate many tasks but want per-task results (map/partition)
                    
                    suspend fun uploadAll(files: List<File>): List<Result<UploadResponse>> = coroutineScope {
                      files.map { file ->
                        async(Dispatchers.IO) {
                          runCatching { upload(file) } // capture per-task result
                        }
                      }.map { it.await() } // list of Result<UploadResponse>
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //async + CoroutineExceptionHandler (logging only)
                    
                    val handler = CoroutineExceptionHandler { _, t ->
                      logError(t)
                    }
                    
                    val scope = CoroutineScope(SupervisorJob() + handler)

                    scope.launch {
                      val d = async { throw RuntimeException("Exception..") }
                      // if we never await, handler may log; but better to await + try/catch
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Coroutine Builders",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "coroutineScope - All-or-Nothing (If any child fails, all other children are cancelled)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "coroutineScope is a suspending function that creates a new coroutine scope and calls the specified coroutine within this scope. This function waits for the completion of its children before it completes itself and returns. When any child coroutine in this scope fails, the scope fails too, canceling all other children.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Scopes created with coroutineScope can have their own lifecycles and can be cancelled independently.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                            import kotlinx.coroutines.*

                            suspend fun main() {
                                coroutineScope {
                                    launch {
                                        delay(1000)
                                        println("Coroutine 1 completed")
                                    }
                                    launch {
                                        delay(2000)
                                        println("Coroutine 2 completed")
                                    }
                                }
                                println("Coroutine scope completed")
                            }
                        """.trimIndent()
                )

                KotlinCode(
                    code = """
                    private val _cartState = mutableStateOf(CartState())
                    val cartState: State<CartState> = _cartState

                    private suspend fun getCart() {
                        coroutineScope {
                            val productFromCartList = async { getCartUseCase() }.await()
                            val totalAmount = async { getTotalAmountUseCase() }.await()

                            withContext(Dispatchers.Main) {
                                productFromCartList.combine(totalAmount) { productFromCart, totalAmount ->
                                    _cartState.value = CartState(productFromCart, totalAmount)
                                }.collect()
                            }
                        }
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "runBlocking",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It runs a new coroutine and blocks the current thread until its completion. (It will block the thread until the coroutine inside it will not execute the completed). This function should not be used inside a coroutine. It will create a Coroutine which block the current main Thread on which its running.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It returns the result of the coroutine, allowing you to execute coroutine code in a blocking manner within a non-coroutine context, such as the main function or a unit test.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It is designed to bridge non-coroutine and coroutine codes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It creates a coroutine scope and runs the operations within it synchronously. This function is often used when you write tests or want to manage asynchronous operations with synchronous logic.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "However, it should be used with caution, as this can block the main thread (such as the UI thread) and cause performance issues.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "By not providing any context, it will get run on the main thread.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Runs a new coroutine and blocks the current thread interruptible until its completion.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This function should not be used from a coroutine.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It is designed to bridge regular blocking code to libraries that are written in suspending style, to be used in main functions and in unit testing classes in Android development. (runBlocking is typically used for writing test code, main functions, or for bridging between blocking and non-blocking code, but it should be avoided in production code as it may block the main thread and degrade app performance).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Avoid using it in regular Android code as it blocks the UI or background threads, which can lead to poor performance.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Don’t use runBlocking inside suspend functions or on the main thread.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        import kotlinx.coroutines.*

                        fun main() {
                            runBlocking {
                                launch {
                                    delay(1000)
                                    println("Coroutine completed")
                                }
                                println("Main function completed")
                            }
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = $$"""
                        //Coarse Whole Request Deadline
                        
                        //Because code in one coroutine runs sequentially and structured concurrency guarantees that no child coroutine is fire and forget, the whole operation can be wrapped in a withTimeout block.
                        fun main() {
                            runCatching {
                                withTimeout(1000) {
                                    //function call
                                }.onFailure { e ->
                                    println("processing cancelled for: $e")
                        		}
                            }
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "withContext",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "WithContext is used to shift a suspend function to a particular Thread that changes the context of execution for the block of code within it. It is used to switch between different dispatchers.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It is a suspending function that is used to shift the execution of the current coroutine on a new CoroutineContext, in most cases, a different dispatcher, such as Dispatchers.IO for I/O-bound operations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It's a suspend function whereas launch and async create a new coroutine and they are not suspend functions.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "It is often used to route and switch asynchronous operations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "So its only task is to shift the suspended functions. WithContext is also suspended in nature and therefore can only be called in the coroutine scope.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "withContext is a suspend function through which we can do a task by providing the Dispatchers on which we want the task to be done.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "We have launched a coroutine on the background thread(Dispatchers.IO). The function apiService.getImage() executes on the background thread. After we get the image url, we want to show the image file in an ImageView. So we change the context(or the underlying thread) of our coroutine with the help of withContext(Dispatchers.Main).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Note that withContext() is a suspending function, which means that it will make the caller coroutine wait until the code within withContext() executes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The withContext function is often used when you need to perform a task on a different thread and then return the result. It changes the dispatcher where the coroutine is executed for the given block of code, then changes it back once the block is completed",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        import kotlinx.coroutines.*

                        fun main() {
                            runBlocking {
                                withContext(Dispatchers.IO) {
                                    // Perform I/O-bound operation
                                    delay(1000)
                                    println("I/O-bound operation completed")
                                }
                            }
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    //API Calling

                    interface MovieRepository {
                        suspend fun getMovies(page: Int, forceFetch: Boolean): Flow<Resource<List<Movie>>>
                    }

                    sealed class Resource<T>(
                        val data: T? = null,
                        val message: String? = null
                    ) {
                        class Success<T>(data: T): Resource<T>(data)
                        class Error<T>(message: String, data: T? = null): Resource<T>(data, message)
                        class Loading<T>(val isLoading: Boolean = true): Resource<T>(null)
                    }

                    internal class RemoteDataSource(private val httpClient: HttpClient) {
                        suspend fun getMovies(page: Int): MoviesResponse = withContext(Dispatchers.IO) {
                            val response = httpClient.get("") {
                                parameter("api_key", "")
                                parameter("page", page)
                            }
                            return@withContext response.body()
                        }
                    }

                    internal class MovieRepositoryImpl(
                        private val remoteDataSource: RemoteDataSource,
                        private val movieDatabase: MovieDatabase
                    ): MovieRepository {

                        override suspend fun getMovie(id: Int): Flow<Resource<Movie>> {
                            return flow {
                                emit(Resource.Loading())
                                try {
                                    val movie = remoteDataSource.getMovie(id).toMovie()
                                    emit(Resource.Success(movie))
                                } catch (e: Exception) {
                                    emit(Resource.Error(e.message ?: "An unknown error occurred"))
                                }
                            }
                        }
                    }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "withContext() Example",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "withContext{} is like temporarily borrowing a chef from one station to work at another station. For instance, you might have a chef from the prep station come over to help with plating for a few minutes. They do their task at the plating station and then return to their original prep station. This is useful when you need to do a specific task on a different thread or station without affecting the overall workflow at that station…",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "withContext Works:",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    public suspend fun <T> withContext(
                        context: CoroutineContext,
                        block: suspend CoroutineScope.() -> T
                    ): T {
                        val oldContext = coroutineContext
                        val newContext = oldContext + context
                        return if (oldContext === newContext) {
                            block()
                        } else {
                            suspendCoroutineUninterceptedOrReturn { uCont ->
                                // Context switching happens here
                                ScopeCoroutine(newContext, uCont).startUndispatchedOrReturn(block)
                            }
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Retrieving the Current Context",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The current coroutine’s context is fetched using `coroutineContext`.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Creating a New Context",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A new context is created by merging the current context with the specified one using `oldContext + context`.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Context Comparison",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If the current and new contexts are identical, the `block` executes directly without switching.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Context Switching",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If the contexts differ, `suspendCoroutineUninterceptedOrReturn` is invoked to suspend the coroutine and resume it in the new context.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Key Class: `ScopeCoroutine`",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The `ScopeCoroutine` class is critical for managing the coroutine’s lifecycle during context switching:",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This class encapsulates the new context and ensures that the coroutine can safely suspend and resume.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                        internal open class ScopeCoroutine<T>(
                            context: CoroutineContext,
                            uCont: Continuation<T>
                        ) : AbstractCoroutine<T>(context, true) {
                            // Handles coroutine lifecycle and context
                        }
                    """.trimIndent()
                )
            }

            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "withContext(NonCancellable)",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )
            }

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When working with structured concurrency, we often encounter situations where certain critical operations must not be cancelled, such as saving data or closing resources gracefully. That's where withContext(NonCancellable) comes into play. By ensuring that these operations complete even when the coroutine scope is cancelled, it becomes a game-changer for building reliable Android apps. This block ensures that essential tasks are completed even during cancellations, ensuring better stability and handling in our applications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Some parts of your code might need to complete even if the coroutine is cancelled. For such situations, you can use withContext(NonCancellable) to ensure certain code always runs regardless of cancellation.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Closing files, network sockets, releasing a resource, or database connections in a finally block.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ensuring a critical database transaction is either committed or rolled back, even if the user navigates away.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Sending a crucial final status or error log to an analytics service.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    import kotlinx.coroutines.*

                    suspend fun performCriticalTask() {
                        withContext(NonCancellable) {
                            // Code that must execute even if the coroutine is cancelled
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun main() = runBlocking {
                        val job = launch {
                            try {
                                repeat(1000) { i ->
                                    println(i) //Job: I'm working
                                    delay(500)
                                }
                            } finally {
                                withContext(NonCancellable) {
                                    println("Job: Cleaning up resources...")
                                    delay(1000) // Simulate cleanup work
                                    println("Job: Cleanup complete.")
                                }
                            }
                        }

                        delay(1300)
                        println("Main: Cancelling job!")
                        job.cancelAndJoin()
                        println("Main: Job is cancelled.")
                    }

                    /*
                    output
                    Job: I'm working 0 ...
                    Job: I'm working 1 ...
                    Job: I'm working 2 ...
                    Main: Cancelling job!
                    Main: Job is cancelled.
                    Job: Cleaning up resources...
                    Job: Cleanup complete.
                    */
                    """.trimIndent()
            )

            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "produce",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )
            }

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Creates a coroutine that produces a stream of values. This is useful for scenarios involving channels and producer-consumer patterns.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    import kotlinx.coroutines.*
                    import kotlinx.coroutines.channels.*

                    fun main() = runBlocking {
                        val producer = produce {
                            for (i in 1..5) {
                                delay(100L)
                                send(i)
                            }
                        }

                        for (x in producer) {
                            println(x)
                        }
                    }
                    """.trimIndent()
            )

            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "actor",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )
            }

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Creates a coroutine-based actor that processes messages. This is useful for managing state in a concurrent environment.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    import kotlinx.coroutines.*
                    import kotlinx.coroutines.channels.*

                    fun main() = runBlocking {
                        val actor = actor<Int> {
                            for (msg in channel) {
                                println("Received")
                            }
                        }

                        actor.send(1)
                        actor.send(2)
                        actor.send(3)

                        actor.close() // Close the actor

                    """.trimIndent()
            )

            SpanText(
                text = "Why do we have to use coroutine builders in Kotlin?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We have to use coroutine builders in Kotlin because they provide the only way to create and control the execution of coroutines. Without these builders (launch, async, withContext), there is no mechanism to initiate, manage, or handle coroutines. They are essential for",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Starting coroutines to perform background or asynchronous tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Controlling execution contexts, ensuring the right dispatcher (thread) is used.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Managing concurrency by structuring how coroutines run and interact with each other.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why Use Coroutine Builders Together?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In real-world applications, there are often situations where you need both blocking and non-blocking behavior depending on the context, especially in testing or console applications. Let’s explore scenario:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In non-UI contexts like tests or console applications, you need a starting point to launch coroutines, but you can’t suspend the main thread directly (as it doesn’t support coroutines natively). Here’s where runBlocking comes in handy.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You can start a coroutine in a blocking way using runBlocking, and inside this, use launch to create non-blocking background tasks.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    fun main() = runBlocking {
                        println("Main thread is blocked")

                        launch {
                            delay(1000)
                            println("Task 1 completed")
                        }

                        launch {
                            delay(500)
                            println("Task 2 completed")
                        }

                        println("Waiting for tasks to complete...")
                    }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "runBlocking blocks the main thread, making sure the program waits for the coroutines to complete. Inside runBlocking, we use launch to create non-blocking coroutines. Even though runBlocking blocks the main thread, launch still allows these coroutines to run concurrently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "GlobalScope Vs Launch",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "GlobalScope creates a coroutine at the global(app) level and this coroutine gets destroyed when the application gets destroyed whereas launch creates a coroutine at the local level and it gets destroyed when the screen gets destroyed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "launch and async",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Both the launch and async are used to launch a coroutine. This enables us to do tasks in parallel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "async can be used to get the result that is not possible with the launch.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "runBlocking Vs launch",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "runBlocking: Blocks the current thread and is generally used to bridge the non-coroutine world (like main thread or tests) with coroutines. Once the coroutine inside runBlocking completes, the program can continue.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "launch: Creates a non-blocking coroutine that runs in the background. You can think of launch as a way to fire off a task and let it run concurrently, without blocking the thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "delay()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The delay function is a type of suspend function, that allows us to pause the execution of a coroutine for a specified amount of time without blocking the underlying thread.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the above diagram, coroutine c1 has called thread. sleep function but it has suspended the main function and all the coroutines associated with it. But delay function has suspended the execution of coroutine c1 only.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Pros",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Lightweight and efficient for fire-and-forget tasks.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "No overhead in managing a result.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Cons",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Lack of result handling may complicate error management.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Careful usage is required to avoid resource leaks.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Delay() vs Thread.sleep()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Delay is a suspend function - It will not block a thread. Sleep - It will block the thread",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Delay the specific coroutine for the mentioned time. Block the thread for the mentioned time.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When delaying, the thread is free to do other work - can jump to another coroutine. Thread is not free to do other work - blocks the whole execution.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Delay will resume from the point where it gets suspended - execution hoping. Remain standing at the point where it gets sleep and stop the whole execution and awake again once free.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "CoroutineScope vs CoroutineContext",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A coroutine scope has a single property i.e. coroutine context.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Holds coroutine-related data such as jobs, dispatchers, and exception handlers.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Every coroutine needs to run in a specific coroutine scope and several coroutines can be started in the same scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If a coroutine is started and some child coroutines are also started in a certain scope, then their job objects will form a parent child hierarchy.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each of the coroutines in the hierarchy run in the same scope and by default, each coroutine inherits the context of the scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Coroutines Scope",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                SpanText(
                    text = "Coroutines are launched within a coroutine scope, which defines their lifecycle and determines where they can run. Common scopes include GlobalScope coroutines that need to run globally and CoroutineScope coroutines tied to a specific context (like an activity or a fragment in Android development).",
                    modifier = Modifier.padding(bottom = 0.3.em)
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Global scope",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Coroutines defined in the global scope are available throughout the app.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "They can be used to perform long-running tasks in the background, such as network requests or database operations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel scope",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel is a class that is responsible for managing and storing UI-related data and business logic, perform long-running tasks and survives configuration changes such as screen rotations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Coroutine scope attached with your View Models. it provide scope to launch coroutine from viewmodel.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Coroutines in this scope will be cancelled automatically when viewModel is cleared. We don’t need to manually cancel the coroutines.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "For example, if a ViewModel is responsible for loading data from a database or network, the associated coroutines would be executed in the ViewModel scope.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    KotlinCode(
                        code = """
                            fun doSomeWork(){
                              viewModelScope.launch {
                                while(true){
                                  println(Thread.currentThread().name)
                                }
                              }
                            }
                        """.trimIndent()
                    )

                    KotlinCode(
                        code = """
                            data class CartItem(
                                val product: Product,
                                val quantity: Int,
                            )

                            private fun decrease(item: CartItem) {
                                viewModelScope.launch {
                                    if (item.quantity == 1) {
                                        return@launch
                                    }
                                }
                            }
                        """
                            .trimIndent()
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Lifecycle scope (lifecycleScope) - (Tied Activity & LifecycleOwner, starts onCreateView() and destroys onDestroyView())",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Tied to the Activity or Fragment lifecycle. The Lifecycle scope is used to perform tasks that are tied to the lifecycle of an Android component, such as an Activity or Fragment.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "lifecycleScope is tied to the LifecycleOwner.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Its lifecycle starts when onCreateView() is called and destroys when onDestroyView() is called.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Cancelled when the lifecycle owner is destroyed. Coroutines in the Lifecycle scope are automatically cancelled when the associated component is destroyed, which helps to prevent memory leaks, cause exceptions and other issues.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = " Useful for work that needs to respond to lifecycle events but does not belong in a ViewModel.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    KotlinCode(
                        code = """
                        //1    
                        lifecycleScope.launch(Dispatchers.IO){
                            performLongRunningOperation()
                        }
                        
                        //2
                        class OrderActivity : AppCompatActivity() {
                            override fun onCreate(savedInstanceState: Bundle?) {
                                super.onCreate(savedInstanceState)
                                lifecycleScope.launch {
                                    repeatOnLifecycle(Lifecycle.State.STARTED) {
                                        viewModel.uiState.collect { state ->
                                            renderState(state)
                                        }
                                    }
                                }
                            }
                        }
                    """.trimIndent()
                    )

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "In this example, the lifecycleScope.launch function launches a new coroutine in the lifecycleScope and executes the code inside the coroutine block asynchronously. When the associated component (such as an Activity or Fragment) is destroyed, the lifecycleScope automatically cancels the coroutine and releases any associated resources.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "viewModelScope - (Tied Fragment’s lifecycle & ViewModel’s lifecycle, Not automatically survives configuration)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "By default, viewModelScope launches coroutines on Dispatchers.Main. When loadContent is called, viewModelScope launches an Activity lifecycle-aware coroutine. Within, scope.launch then calls on doHeavyWork to start. Coroutines launched in viewModel are automatically canceled when the viewModel is cleared, making it ideal for ViewModel-based coroutines.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Tied to the ViewModel lifecycle. Cancelled when onCleared is called, which happens when the associated screen is permanently gone, not on configuration changes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Main scope",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The Main scope is used to perform tasks that update the user interface, such as updating a TextView or ImageView.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Coroutines in the Main scope are automatically executed on the main thread, which is required for updating the UI.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CoroutineScope",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "A CoroutineScope is an interface that defines a set of functions for launching coroutines and managing their lifecycle. It represents a context in which coroutines can be launched and executed and provides a way to cancel them when they are no longer needed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "CoroutineScope is a more general-purpose scope that can be used to launch coroutines outside of the ViewModel and lifecycle owner.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Scope Android",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "(Some may argue that Koin is a service locator, not dependency injection, but let’s not split hairs right now)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Let’s chat about something that doesn’t always get the spotlight it deserves: Scopes in DI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Scopes control the lifecycle of dependencies in your application. They help manage memory by ensuring that instances are created, shared, and destroyed at appropriate times.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Without scopes, you could end up with instances living longer than needed, leading to memory leaks, or creating too many instances, which can degrade performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Singleton Scope",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This is used for dependencies that should live as long as the application does. Perfect for app-wide services like network clients or analytics instances.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Activity Scope",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "For dependencies that are relevant to an activity’s lifecycle. This ensures that these dependencies don’t outlive the activity, preventing memory leaks.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Fragment Scope",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Similar to Activity Scope, but tied to the lifecycle of a Fragment. It helps manage dependencies that are specific to a particular fragment.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel Scope",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This scope is tied to the lifecycle of a ViewModel. It’s useful for dependencies that need to survive configuration changes, like during screen rotations.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Structured concurrency",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Coroutines follow a principle of structured concurrency which means new coroutines can only be launched in a specific CoroutineScope which controls the lifetime of the coroutine. The above example shows that runBlocking establishes the corresponding scope and that is why the previous example waits until World! is printed after a second's delay and only then exits.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In a real application, you will be launching a lot of coroutines. Structured concurrency ensures that they are not lost and do not leak. An outer scope cannot be completed until all its children’s coroutines are complete. Structured concurrency also ensures that any errors in the code are properly reported and are never lost.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Scope builder",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can create our coroutine scope by using coroutineScope. Difference b/w runBlocking and coroutineScope is: earlier one blocks the current thread and waits for all its children to complete. while later one suspends, releasing the underlying thread for other usages.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Using runBlocking more than one time may create deadlock while coroutineScope can be used inside any suspending function to perform multiple concurrent operations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // Sequentially executes doWorld followed by "Done"

                    fun main() = runBlocking {
                        doWorld()
                        println("Done")
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Concurrently executes both sections

                    suspend fun doWorld() = coroutineScope {
                        launch {
                            delay(2000L)
                            println("World 2")
                        }
                        launch {
                            delay(1000L)
                            println("World 1")
                        }
                        println("Hello")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "suspend vs async",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Function Modifier",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Permission to pause execution",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Sequential by default",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Returns the result directly",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Coroutine Builder",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Starts a task that returns a Deferred value",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Enables parallel execution",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Returns a Deferred <T> (awaitable)",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }
        }
    }
}
